import { HHBox, HHBoxProps } from '@hinge-health/react-component-library';
import { Theme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';

/**
 * Card radius from the design spec (10px cards, 4px controls). Kept as a
 * constant so every surface in the module rounds the same way.
 */
export const CARD_BORDER_RADIUS_PX = 10;

export interface SurfaceCardProps extends Omit<HHBoxProps, 'sx'> {
  /** Lifts the card on hover; only for cards that act as buttons or links. */
  isInteractive?: boolean;
  /** For a card inside another card: keeps the border, drops the shadow. */
  isNested?: boolean;
  sx?: SystemStyleObject<Theme>;
  /** for cards rendered as router links */
  to?: string;
}

/**
 * The module's one card surface: paper background, hairline divider border,
 * 10px radius, resting shadow. Compose everything card-shaped from this so
 * the throughput panel, table card, and future provider cards share edges.
 */
export const SurfaceCard = ({
  isInteractive = false,
  isNested = false,
  sx,
  children,
  ...boxProps
}: SurfaceCardProps): JSX.Element => (
  <HHBox
    {...boxProps}
    sx={(theme: Theme): SystemStyleObject<Theme> => ({
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: `${CARD_BORDER_RADIUS_PX}px`,
      boxShadow: isNested ? 'none' : theme.shadows[1],
      transition: 'box-shadow 0.2s ease-in-out',
      ...(isInteractive && {
        cursor: 'pointer',
        '&:hover': { boxShadow: theme.shadows[4] },
      }),
      ...sx,
    })}
  >
    {children}
  </HHBox>
);
